import { z } from 'zod'

// Environment variable schema
export const EnvSchema = z.object({
  // Core configuration
  MIHARU_ADAPTER: z.enum(['supabase', 'sqlite', 'memory']).optional(),
  MIHARU_INSTANCE_ID: z.string().optional(),
  MIHARU_LOG_LEVEL: z.enum(['debug', 'info', 'warn', 'error']).default('info'),
  
  // Supabase configuration
  MIHARU_SUPABASE_URL: z.string().url().optional(),
  MIHARU_SUPABASE_ANON_KEY: z.string().optional(),
  MIHARU_SUPABASE_SERVICE_ROLE_KEY: z.string().optional(),
  
  // SQLite configuration
  MIHARU_SQLITE_FILENAME: z.string().optional(),
  MIHARU_SQLITE_READONLY: z.string().transform(val => val === 'true').optional(),
  
  // Performance configuration
  MIHARU_BATCH_SIZE: z.string().transform(Number).pipe(z.number().min(1).max(1000)).optional(),
  MIHARU_FLUSH_INTERVAL: z.string().transform(Number).pipe(z.number().min(100).max(60000)).optional(),
  MIHARU_MEMORY_THRESHOLD_MB: z.string().transform(Number).pipe(z.number().min(1).max(100)).optional(),
  
  // Security configuration
  MIHARU_ENABLE_ENCRYPTION: z.string().transform(val => val === 'true').optional(),
  MIHARU_ENCRYPTION_KEY: z.string().min(32).optional(),
  MIHARU_SANITIZE_PII: z.string().transform(val => val === 'true').default('true'),
  
  // Feature flags
  MIHARU_ENABLE_STREAMING: z.string().transform(val => val === 'true').default('true'),
  MIHARU_ENABLE_BATCHING: z.string().transform(val => val === 'true').default('true'),
  MIHARU_ENABLE_AUTO_RECOVERY: z.string().transform(val => val === 'true').default('true'),
  MIHARU_ENABLE_PERFORMANCE_MONITORING: z.string().transform(val => val === 'true').default('false'),
  
  // Provider configuration
  MIHARU_ENABLED_PROVIDERS: z.string().transform(val => val.split(',')).pipe(
    z.array(z.enum(['openai', 'anthropic', 'google'])).optional()
  ).optional(),
  
  // Node.js environment
  NODE_ENV: z.enum(['development', 'production', 'test']).default('development'),
})

// Adapter-specific options schemas
export const SupabaseOptionsSchema = z.object({
  url: z.string().url(),
  apiKey: z.string().min(1),
  serviceRoleKey: z.string().optional(),
  connectionTimeout: z.number().min(100).max(30000).default(5000),
  maxRetries: z.number().min(0).max(10).default(3),
  enableRLS: z.boolean().default(true),
})

export const SQLiteOptionsSchema = z.object({
  filename: z.string().min(1).default('./miharu.db'),
  readonly: z.boolean().default(false),
  enableWAL: z.boolean().default(true),
  busyTimeout: z.number().min(100).max(30000).default(5000),
  maxRetries: z.number().min(0).max(10).default(3),
})

export const MemoryOptionsSchema = z.object({
  maxEntries: z.number().min(100).max(100000).default(10000),
  enablePersistence: z.boolean().default(false),
  persistenceFile: z.string().optional(),
})

// Main configuration schema
export const MiharuConfigSchema = z.object({
  // Instance settings
  instanceId: z.string().optional(),
  
  // Adapter settings
  adapter: z.enum(['supabase', 'sqlite', 'memory']),
  options: z.union([
    SupabaseOptionsSchema,
    SQLiteOptionsSchema,
    MemoryOptionsSchema,
    z.record(z.unknown()),
  ]).optional(),
  
  // Batch processing settings
  batchConfig: z.object({
    minSize: z.number().min(1).max(100).default(10),
    maxSize: z.number().min(10).max(1000).default(100),
    flushInterval: z.number().min(100).max(60000).default(5000),
    memoryThresholdMB: z.number().min(1).max(100).default(8),
    overflowStrategy: z.enum(['flush', 'drop']).default('flush'),
  }).optional(),
  
  // Provider settings
  enabledProviders: z.array(z.enum(['openai', 'anthropic', 'google'])).default(['openai', 'anthropic', 'google']),
  
  // Session settings
  sessionConfig: z.object({
    inactivityTimeout: z.number().min(60000).max(7200000).default(1800000), // 1 min to 2 hours
    extendOnActiveCall: z.boolean().default(true),
    detectionStrategy: z.enum(['context', 'timestamp', 'process']).default('context'),
  }).optional(),
  
  // Retry settings
  retryConfig: z.object({
    maxRetries: z.number().min(0).max(10).default(3),
    backoffType: z.enum(['exponential', 'linear']).default('exponential'),
    initialDelayMs: z.number().min(100).max(10000).default(1000),
    maxDelayMs: z.number().min(1000).max(60000).default(30000),
    jitterMs: z.number().min(0).max(1000).default(100),
    fallbackDir: z.string().default('./miharu-backup'),
  }).optional(),
  
  // Custom pricing settings
  customPricing: z.record(
    z.string(), // provider
    z.record(z.string(), z.object({ // model
      input: z.number().min(0),
      output: z.number().min(0),
    }))
  ).optional(),
  
  // Performance settings
  performanceLimits: z.object({
    maxLatencyIncreasePercent: z.number().min(0).max(50).default(5),
    maxMemoryUsageMB: z.number().min(1).max(100).default(10),
    alertThresholdMB: z.number().min(1).max(50).default(8),
    measurementInterval: z.number().min(500).max(10000).default(1000),
  }).optional(),
  
  // Data retention settings
  retentionPolicy: z.object({
    traces: z.object({
      maxAgeDays: z.number().min(1).max(365).default(90),
      cleanupInterval: z.number().min(3600000).max(604800000).default(86400000), // 1 hour to 1 week
    }).optional(),
    backupFiles: z.object({
      maxAgeDays: z.number().min(1).max(30).default(7),
      maxSizeMB: z.number().min(1).max(1000).default(100),
      maxFiles: z.number().min(1).max(100).default(10),
    }).optional(),
    performanceMetrics: z.object({
      maxAgeDays: z.number().min(1).max(90).default(30),
      aggregationInterval: z.number().min(300000).max(86400000).default(3600000), // 5 min to 24 hours
    }).optional(),
  }).optional(),
  
  // Security settings
  security: z.object({
    enableEncryption: z.boolean().default(false),
    encryptionKey: z.string().min(32).optional(),
    sanitizePII: z.boolean().default(true),
    piiPatterns: z.array(z.string()).optional(),
    filePermissions: z.string().regex(/^[0-7]{3}$/).default('600'),
  }).optional(),
  
  // Feature flags
  featureFlags: z.object({
    enableStreaming: z.boolean().default(true),
    enableBatching: z.boolean().default(true),
    enableAutoRecovery: z.boolean().default(true),
    enablePerformanceMonitoring: z.boolean().default(false),
    enableDistributedTracing: z.boolean().default(false),
    enableMetricsDashboard: z.boolean().default(false),
  }).optional(),
  
  // Logging configuration
  logging: z.object({
    level: z.enum(['debug', 'info', 'warn', 'error']).default('info'),
    format: z.enum(['json', 'pretty']).default('pretty'),
    destination: z.enum(['console', 'file', 'both']).default('console'),
    filename: z.string().optional(),
    maxFileSize: z.number().min(1024).max(104857600).default(10485760), // 1KB to 100MB
    maxFiles: z.number().min(1).max(10).default(3),
  }).optional(),
})

// Type exports
export type EnvConfig = z.infer<typeof EnvSchema>
export type MiharuConfig = z.infer<typeof MiharuConfigSchema>
export type SupabaseOptions = z.infer<typeof SupabaseOptionsSchema>
export type SQLiteOptions = z.infer<typeof SQLiteOptionsSchema>
export type MemoryOptions = z.infer<typeof MemoryOptionsSchema>

// Configuration error class
export class MiharuConfigError extends Error {
  constructor(
    message: string,
    public readonly errors: Array<{ path: string; message: string; received?: unknown }>
  ) {
    super(`${message}: ${errors.map(e => `${e.path}: ${e.message}`).join(', ')}`)
    this.name = 'MiharuConfigError'
  }
}